import { Checkbox, EmailInput, TextInput } from '@designsforhealth/dfh-react-components'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { UseFormMethods } from 'react-hook-form'
import { firstValueFrom } from 'rxjs'
import styled from 'styled-components'
import { useReCaptcha } from '../../lib/auth/hooks/useReCaptcha'
import { useConfigParams } from '../../lib/config'
import {
  RegistrationData,
  RegistrationFieldValues,
  RegistrationPatientUserMetadata,
  SubmitDataValidationHandler,
  termsAndConditionsMessage,
  useRegistrationForm,
  useSetLoginFactory,
  validatePasswordsMatch,
  validateTerms,
} from '../../lib/registration'
import { useLoginAfterRegistration } from '../../lib/registration/request/login-after'
import {
  patientRegistrationRequest$,
  setPatientRegistrationInput,
  useLatestPatientRegistrationInput,
} from '../../lib/registration/request/patient'
import {
  formDisabledStates,
  formProcessingStates,
  useRegistrationState,
} from '../../lib/registration/state'
import { generatePassword } from '../../lib/registration/util'
import { LoginAfterRegistrationErrorMessage } from './LoginAfterRegistrationErrorMessage'
import { RegistrationFormHelpButton } from './RegistrationFormHelpButton'
import { RegistrationFormPasswordInputs } from './RegistrationFormPasswordInputs'
import { RegistrationFormPatientAgreement } from './RegistrationFormPatientAgreement'
import { RegistrationFormReCaptcha } from './RegistrationFormReCaptcha'
import { RegistrationFormSimpleTerms } from './RegistrationFormSimpleTerms'
import { RegistrationFormSubmit, RegistrationFormSubmitProps } from './RegistrationFormSubmit'
import { RegistrationMessage } from './RegistrationMessage'
import * as Styled from './styled'
import { RegistrationUserActionRequired } from './RegistrationUserActionRequired'
import {
  EMAIL_MAX_LENGTH,
  emailMaxLengthRule,
  FIRST_NAME_MAX_LENGTH,
  firstNameMaxLengthRule,
  LAST_NAME_MAX_LENGTH,
  lastNameMaxLengthRule,
} from '../../lib/form/fieldLengthRules'

const { Title, TitleHelper } = Styled

export { Title, TitleHelper }

export interface RegistrationFormPatientProps {
  setSubmitting?: (submitting: boolean) => void
  isGuestCheckoutEnabled?: boolean
  allowPatientZipCode?: boolean
  isUseZipCode: boolean
  setIsUseZipCode: (isUseZipCode: boolean) => void
  submitButton?: {
    text: string
    variant: RegistrationFormSubmitProps['variant']
  }
  onGuestCheckoutChange?: (guestCheckout: boolean) => void
}

type RegistrationPatientFieldValues = RegistrationFieldValues & {
  practitionerCode?: string
  zipCode?: string
  guestCheckout?: boolean
  hasPractitioner?: 'yes' | 'no' | null
  registrationTrigger?: string
}

const sanitizeOptionalString = (value: string | undefined): string | undefined => {
  return typeof value === 'string' ? value.trim() || undefined : undefined
}

const parseFieldValuesToRequestData = (
  {
    firstName,
    lastName,
    email,
    password,
    practitionerCode,
    zipCode,
    hasPractitioner,
    guestCheckout,
    registrationTrigger,
  }: RegistrationPatientFieldValues,
  {
    setError,
  }: {
    setError: UseFormMethods<RegistrationPatientFieldValues>['setError']
  }
): (RegistrationData & RegistrationPatientUserMetadata) | undefined => {
  const commonData = {
    firstName,
    lastName,
    email,
    type: 'patient',
    has_practitioner: hasPractitioner ? hasPractitioner : undefined,
    practitioner_code: sanitizeOptionalString(practitionerCode),
    zip_code: sanitizeOptionalString(zipCode),
    registration_trigger: sanitizeOptionalString(registrationTrigger),
  } as const
  if (guestCheckout) {
    return {
      ...commonData,
      password: generatePassword(),
      guest_checkout: new Date().toISOString(),
    }
  }
  // should not occur, but check password for sanity and type safety
  if (!password) {
    setError('password', { type: 'validate', message: 'Required' })
    return
  }
  return {
    ...commonData,
    password,
  }
}

export const GuestCheckoutCheckbox = styled(Checkbox)`
  margin-bottom: 20px;
`

export const validatePractitionerCode: SubmitDataValidationHandler<RegistrationPatientFieldValues> =
  (data) =>
    !sanitizeOptionalString(data.practitionerCode)
      ? [
          {
            type: 'error',
            name: 'practitionerCode',
            details: 'Please enter your Practitioner Code.',
          },
        ]
      : []

export const validatePractitionerOrZipCode: SubmitDataValidationHandler<RegistrationPatientFieldValues> =
  (data) =>
    !sanitizeOptionalString(data.practitionerCode) && !sanitizeOptionalString(data.zipCode)
      ? [
          {
            type: 'error',
            name: 'practitionerOrZipCode',
            details:
              'Please enter your Practitioner Code. If you do not have a practitioner, enter your Zip Code',
          },
        ]
      : []

export const validateHasPractitioner: SubmitDataValidationHandler<RegistrationPatientFieldValues> =
  (data) => {
    return !data.hasPractitioner
      ? [
          {
            type: 'error',
            name: 'hasPractitioner',
            details: 'Please indicate whether you have a practitioner.',
          },
        ]
      : []
  }

export const validatePatientAgreement: SubmitDataValidationHandler = (data) =>
  !data.patientagreement ? [termsAndConditionsMessage] : []

export const RegistrationFormPatient: FC<RegistrationFormPatientProps> = ({
  children,
  setSubmitting = () => {
    // noop
  },
  allowPatientZipCode = false,
  isUseZipCode,
  setIsUseZipCode,
  isGuestCheckoutEnabled = false,
  submitButton: { text: submitButtonText, variant: submitButtonVariant } = {},
  onGuestCheckoutChange = () => null,
}) => {
  const { practitionerCode } = useConfigParams()
  useLatestPatientRegistrationInput()
  useLoginAfterRegistration()
  useSetLoginFactory()

  const { captchaError, validateCaptcha, reCaptchaProps } = useReCaptcha()

  const [hasPractitionerSelected, setHasPractitionerSelected] = useState<boolean>(false)

  const submitDataValidationHandlers = useMemo<
    ReadonlyArray<SubmitDataValidationHandler<RegistrationPatientFieldValues>>
  >(
    () => [
      validatePasswordsMatch,
      allowPatientZipCode && !hasPractitionerSelected
        ? validateHasPractitioner
        : allowPatientZipCode
        ? validatePractitionerOrZipCode
        : validatePractitionerCode,
      validateTerms,
      validatePatientAgreement,
    ],
    [allowPatientZipCode, hasPractitionerSelected]
  )

  const [
    { messages, dismissMessage, patterns },
    {
      register,
      watch,
      handleSubmit,
      errors,
      setError,
      formState: { isSubmitting },
    },
  ] = useRegistrationForm<RegistrationPatientFieldValues>({
    defaultValues: { practitionerCode },
    submitDataValidationHandlers,
  })

  const formErrorsAlertRef = useRef<HTMLFormElement>(null)

  const guestCheckoutValue = watch('guestCheckout') ?? false
  const hasPractitionerValue = watch('hasPractitioner') ?? null

  const isZipCodeRegistration = allowPatientZipCode && isUseZipCode
  const isCodeInUrl =
    URLSearchParams && new URLSearchParams(window.location.search).has('practitionerCode')

  const scrollErrorsRefIntoView = () => {
    const formErrorsEl = formErrorsAlertRef.current
    if (formErrorsEl) {
      formErrorsEl.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const onSubmit = useCallback(
    async (fieldValues: RegistrationPatientFieldValues) => {
      const isValidCaptcha = !!validateCaptcha()
      if (!isValidCaptcha) {
        return
      }
      const requestData = parseFieldValuesToRequestData(
        {
          ...fieldValues,
          registrationTrigger: isZipCodeRegistration
            ? 'PROVIDED_BY_ZIP'
            : isCodeInUrl
            ? 'PROVIDED_BY_URL'
            : 'PROVIDED_BY_USER',
        },
        { setError }
      )
      if (!requestData) {
        return
      }
      setPatientRegistrationInput({ input: requestData, isGuestCheckoutEnabled })
      await firstValueFrom(patientRegistrationRequest$)
    },
    [isGuestCheckoutEnabled, setError, validateCaptcha, isZipCodeRegistration, isCodeInUrl]
  )

  const { name: registrationState } = useRegistrationState()
  const processing = isSubmitting || formProcessingStates.includes(registrationState)
  const disabled = processing || formDisabledStates.includes(registrationState)

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      scrollErrorsRefIntoView()
    }
  }, [errors])

  useEffect(() => {
    setSubmitting(processing)
  }, [setSubmitting, processing])

  useEffect(() => {
    onGuestCheckoutChange(guestCheckoutValue)
  }, [guestCheckoutValue, onGuestCheckoutChange])

  useEffect(() => {
    if (hasPractitionerValue) {
      setHasPractitionerSelected(true)
    }
  }, [hasPractitionerValue])

  useEffect(() => {
    const radioButtonNoPractitioner = document.getElementById('has_practitioner_no')
    radioButtonNoPractitioner?.addEventListener('click', () => {
      if (!isZipCodeRegistration) {
        setIsUseZipCode(allowPatientZipCode ? !isUseZipCode : false)
      }
    })
  }, [allowPatientZipCode, isUseZipCode, isZipCodeRegistration, setIsUseZipCode])

  const handleOnClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setIsUseZipCode(allowPatientZipCode ? !isUseZipCode : false)
    },
    [setIsUseZipCode, allowPatientZipCode, isUseZipCode]
  )

  const userActionRequiredMessage = useMemo(
    () => messages.find((message) => !!message.actions?.includes('user_action_required')),
    [messages]
  )

  if (userActionRequiredMessage) {
    return <RegistrationUserActionRequired message={userActionRequiredMessage} />
  }

  return (
    <Styled.RegistrationForm noValidate onSubmit={handleSubmit(onSubmit)} ref={formErrorsAlertRef}>
      {children}
      {messages.map((message, index) => (
        <RegistrationMessage
          key={index}
          message={message}
          onDismiss={() => dismissMessage(message)}
        />
      ))}
      {registrationState === 'login_error' && <LoginAfterRegistrationErrorMessage />}
      <div>
        {errors && Object.keys(errors).length > 0 && (
          <Styled.Alert type="error">
            Please review the form for any missing or required field(s).
          </Styled.Alert>
        )}
        {isGuestCheckoutEnabled && (
          <Styled.FieldsRow>
            <GuestCheckoutCheckbox
              label="Use Guest Checkout Instead"
              name="guestCheckout"
              register={register}
              defaultChecked={false}
              disabled={disabled}
              value=""
            />
          </Styled.FieldsRow>
        )}
        <Styled.FieldsRow>
          <Styled.HalfColumn>
            <TextInput
              autoComplete="given-name"
              label="First name"
              name="firstName"
              register={register}
              errors={errors}
              required
              disabled={disabled}
              maxLength={FIRST_NAME_MAX_LENGTH}
              validations={firstNameMaxLengthRule}
              data-cy="registration-patient-input-first-name"
            />
          </Styled.HalfColumn>
          <Styled.HalfColumn>
            <TextInput
              autoComplete="family-name"
              label="Last name"
              name="lastName"
              register={register}
              errors={errors}
              required
              disabled={disabled}
              maxLength={LAST_NAME_MAX_LENGTH}
              validations={lastNameMaxLengthRule}
              data-cy="registration-patient-input-last-name"
            />
          </Styled.HalfColumn>
        </Styled.FieldsRow>
        <Styled.FieldsRow>
          <EmailInput
            autoComplete="email"
            label="Email Address"
            name="email"
            register={register}
            errors={errors}
            required
            disabled={disabled}
            maxLength={EMAIL_MAX_LENGTH}
            validations={{ ...emailMaxLengthRule, pattern: patterns.email }}
            data-cy="registration-patient-input-email"
          />
        </Styled.FieldsRow>
        {!guestCheckoutValue && (
          <RegistrationFormPasswordInputs
            disabled={disabled}
            errors={errors}
            register={register}
            watch={watch}
          />
        )}
        {practitionerCode && (
          <input
            type="hidden"
            name="practitionerCode"
            defaultValue={practitionerCode}
            ref={register}
          />
        )}

        {!practitionerCode && (
          <Styled.ContentWrapper $hasPractitionerSelected={!!hasPractitionerValue}>
            <Styled.RadioButtonGroupTitle>
              Are you working with a practitioner?
            </Styled.RadioButtonGroupTitle>
            <Styled.FieldsRow>
              <Styled.RadioButtonGroup
                name="hasPractitioner"
                radioButtonOptions={[
                  {
                    id: 'has_practitioner_no',
                    label: (
                      <>
                        No, I&apos;m&nbsp;<Styled.Bold>looking to find</Styled.Bold>&nbsp;a new
                        practitioner
                      </>
                    ),
                    value: 'no',
                  },
                  {
                    id: 'has_practitioner_yes',
                    label: (
                      <>
                        Yes, I&apos;m&nbsp;<Styled.Bold>already working</Styled.Bold>&nbsp;with a
                        practitioner
                      </>
                    ),
                    value: 'yes',
                  },
                ]}
                register={register}
              />
            </Styled.FieldsRow>
            {hasPractitionerValue && (
              <>
                <Styled.Title>
                  {isZipCodeRegistration && hasPractitionerValue === 'no'
                    ? 'Find a practitioner'
                    : 'My practitioner'}
                </Styled.Title>
                <Styled.FieldsRow $hide={!isZipCodeRegistration}>
                  <Styled.HalfColumn>
                    <Styled.CustomTextInput
                      label="Zip Code"
                      name="zipCode"
                      register={register}
                      errors={errors}
                      required={isZipCodeRegistration}
                      disabled={disabled || !isZipCodeRegistration}
                    />
                    <RegistrationFormHelpButton>
                      We will provide suggestions of practitioners for your zip code.
                      {hasPractitionerValue === 'yes' && (
                        <>
                          <br /> Already have a practitioner code? You can enter it instead.
                        </>
                      )}
                    </RegistrationFormHelpButton>
                  </Styled.HalfColumn>
                </Styled.FieldsRow>
                <Styled.FieldsRow $hide={isZipCodeRegistration}>
                  <Styled.HalfColumn>
                    <Styled.CustomTextInput
                      label="Practitioner Code"
                      name="practitionerCode"
                      register={register}
                      errors={errors}
                      disabled={disabled || isZipCodeRegistration}
                    />
                    <RegistrationFormHelpButton>
                      You must be linked to a practitioner before ordering with Designs for health.
                      <br /> Click the link below if you do not have a DFH Practitioner.
                    </RegistrationFormHelpButton>
                  </Styled.HalfColumn>
                </Styled.FieldsRow>

                {allowPatientZipCode && hasPractitionerValue === 'yes' && (
                  <Styled.SwitchZipOrCodeButtonContainer>
                    <Styled.SwitchZipOrCodeButton type="button" onClick={handleOnClick}>
                      {isZipCodeRegistration ? (
                        <>
                          <Styled.Bold>Click here</Styled.Bold> to use a practitioner code
                        </>
                      ) : (
                        <>
                          Don&apos;t have a practitioner code? <Styled.Bold>Click here</Styled.Bold>{' '}
                          to use your zip code instead
                        </>
                      )}
                    </Styled.SwitchZipOrCodeButton>
                  </Styled.SwitchZipOrCodeButtonContainer>
                )}
              </>
            )}
          </Styled.ContentWrapper>
        )}

        <Styled.FieldsRow>
          <RegistrationFormSimpleTerms
            register={register}
            linkComponents={[
              Styled.PrivacyNoticeLink,
              Styled.TermsOfUseLink,
              Styled.TermsOfSaleLink,
            ]}
          />
        </Styled.FieldsRow>

        <Styled.FieldsRow $gutter>
          <RegistrationFormPatientAgreement register={register} />
        </Styled.FieldsRow>

        <RegistrationFormReCaptcha reCaptchaProps={reCaptchaProps} captchaError={captchaError} />

        <RegistrationFormSubmit
          disabled={disabled}
          processing={processing}
          variant={submitButtonVariant}
        >
          {guestCheckoutValue ? 'Proceed to Checkout' : submitButtonText || 'Register'}
        </RegistrationFormSubmit>
      </div>
    </Styled.RegistrationForm>
  )
}
